import React, { useState } from 'react';
import { BowelMovement } from './types';
import { BowelMovementForm } from './BowelMovementForm';
import { EmptyState } from './components/common/EmptyState';

interface BowelMovementHistoryProps {
  entries: BowelMovement[];
  onUpdate: (id: string, data: BowelMovement) => void;
  onDelete: (id: string) => void;
  filter: 'all' | 'today' | 'week' | 'month' | 'quarter' | 'year';
  onAddFirst?: () => void;
}

const BookIconEmpty = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
  </svg>
);

export const BowelMovementHistory: React.FC<BowelMovementHistoryProps> = ({ 
  entries, 
  onUpdate, 
  onDelete,
  filter,
  onAddFirst
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleEdit = (entry: BowelMovement) => {
    setEditingId(entry.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleSaveEdit = (data: BowelMovement) => {
    if (editingId) {
      onUpdate(editingId, { ...data, id: editingId });
      setEditingId(null);
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten wpis?')) {
      onDelete(id);
    }
  };

  if (editingId) {
    const entryToEdit = entries.find(e => e.id === editingId);
    if (entryToEdit) {
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Edytuj wpis</h3>
          <BowelMovementForm 
            initialData={entryToEdit}
            onSubmit={handleSaveEdit}
            onCancel={handleCancelEdit}
          />
        </div>
      );
    }
  }

  if (entries.length === 0) {
    const isAllFilter = filter === 'all';
    return (
      <EmptyState
        icon={<BookIconEmpty />}
        title={isAllFilter ? 'Brak wpisów' : 'Brak wpisów dla wybranego okresu'}
        description={
          isAllFilter
            ? 'Nie masz jeszcze żadnych wpisów o wypróżnieniach. Zacznij śledzić swoje objawy aby zobaczyć wzorce i trendy.'
            : `Brak wpisów w tym okresie. Zmień filtr lub dodaj nowe wypróżnienie.`
        }
        actionLabel={isAllFilter ? 'Dodaj pierwsze wypróżnienie' : undefined}
        onAction={isAllFilter ? onAddFirst : undefined}
      />
    );
  }

  return (
    <>
      {/* Mobile: karty (vertical layout) */}
      <div className="block md:hidden space-y-3">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
          >
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-medium text-gray-900">{entry.date}</div>
                  <div className="text-xs text-gray-500">{entry.time}</div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onClick={() => handleEdit(entry)}
                    className="px-3 py-2 bg-[#10B981] text-white rounded-lg text-xs font-medium min-h-[44px] flex items-center"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="px-3 py-2 bg-[#f44336] text-white rounded-lg text-xs font-medium min-h-[44px] flex items-center"
                  >
                    Usuń
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 text-sm">
                <div><span className="text-gray-500">Konsystencja:</span> {entry.consistency}</div>
                <div><span className="text-gray-500">Ilość:</span> {entry.amount}</div>
                <div className="col-span-2">
                  <span className="text-gray-500">Krew:</span>{' '}
                  {entry.hasBlood && entry.bloodAmount ? `⚠️ ${entry.bloodAmount}/5 (${entry.bloodType || '-'})` : '-'}
                </div>
                <div><span className="text-gray-500">Śluz:</span> {entry.hasMucus && entry.mucusAmount ? `${entry.mucusAmount}/5` : '-'}</div>
                <div><span className="text-gray-500">Ból:</span> {entry.pain}/5</div>
                <div><span className="text-gray-500">Gazy:</span> {entry.hasGas && entry.gasIntensity ? `${entry.gasIntensity}/5` : '-'}</div>
                <div><span className="text-gray-500">Pilność:</span> {entry.urgency}/5</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: tabela */}
      <div className="hidden md:block overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b-2 border-gray-200">
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Data i czas</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Konsystencja</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Ilość</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Krew</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Śluz</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Ból</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Gazy</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-left text-xs font-semibold text-gray-700">Pilność</th>
            <th className="px-2 py-2 md:px-3 md:py-3 text-center text-xs font-semibold text-gray-700">Akcje</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry, index) => (
            <tr 
              key={entry.id} 
              className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
            >
              <td className="px-2 py-2 md:px-3 md:py-3">
                <div className="text-sm font-medium text-gray-900">{entry.date}</div>
                <div className="text-xs text-gray-500">{entry.time}</div>
              </td>
              <td className="px-2 py-2 md:px-3 md:py-3 text-sm text-gray-700">{entry.consistency}</td>
              <td className="px-2 py-2 md:px-3 md:py-3 text-sm text-gray-700">{entry.amount}</td>
              <td className="px-2 py-2 md:px-3 md:py-3">
                {entry.hasBlood && entry.bloodAmount && entry.bloodType ? (
                  <div className="text-sm">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#FFEDED] text-[#FF4444]">
                      ⚠️ {entry.bloodAmount}/5
                    </span>
                    <div className="text-xs text-gray-500 mt-1">({entry.bloodType})</div>
                  </div>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-2 py-2 md:px-3 md:py-3">
                {entry.hasMucus && entry.mucusAmount ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#E8F5ED] text-[#00A86B]">
                    {entry.mucusAmount}/5
                  </span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-2 py-2 md:px-3 md:py-3 text-sm text-gray-700">{entry.pain}/5</td>
              <td className="px-2 py-2 md:px-3 md:py-3">
                {entry.hasGas && entry.gasIntensity ? (
                  <span className="text-sm text-gray-700">{entry.gasIntensity}/5</span>
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </td>
              <td className="px-2 py-2 md:px-3 md:py-3 text-sm text-gray-700">{entry.urgency}/5</td>
              <td className="px-2 py-2 md:px-3 md:py-3">
                <div className="flex gap-1.5 md:gap-2 justify-center flex-wrap">
                  <button
                    onClick={() => handleEdit(entry)}
                    className="px-3 py-1.5 md:px-4 md:py-2 bg-[#10B981] text-white rounded-lg text-xs font-medium hover:bg-[#059669] transition-colors"
                  >
                    Edytuj
                  </button>
                  <button
                    onClick={() => handleDelete(entry.id)}
                    className="px-3 py-1.5 md:px-4 md:py-2 bg-[#f44336] text-white rounded-lg text-xs font-medium hover:bg-[#d32f2f] transition-colors"
                  >
                    Usuń
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  );
};
